import { flushSync } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import InvoiceForm from "./components/InvoiceForm";
import InvoicePreview from "./components/InvoicePreview";
import InvoicePDF from "./components/InvoicePDF";
import PrintActions from "./components/PrintActions";
import {
  createBlankItem,
  createDefaultInvoice,
  createInvoiceNumber,
  getCurrencyOption,
} from "./data/invoiceDefaults";
import { validateInvoice } from "./utils/validation";

function App() {
  const [invoice, setInvoice] = useState(() => createDefaultInvoice());
  const [hasEdited, setHasEdited] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const validationMessages = useMemo(() => validateInvoice(invoice), [invoice]);
  const visibleValidationMessages = showValidation ? validationMessages : [];

  useEffect(() => {
    const updatePrintTimestamp = () => {
      setInvoice((current) => ({
        ...current,
        print: {
          printedAt: new Date().toISOString(),
        },
      }));
    };

    window.addEventListener("beforeprint", updatePrintTimestamp);

    return () => window.removeEventListener("beforeprint", updatePrintTimestamp);
  }, []);

  const commitInvoice = (updater) => {
    setHasEdited(true);
    setInvoice(updater);
  };

  const updateClient = (field, value) => {
    commitInvoice((current) => ({
      ...current,
      client: {
        ...current.client,
        [field]: value,
      },
    }));
  };

  const updateInfo = (field, value) => {
    commitInvoice((current) => ({
      ...current,
      info: {
        ...current.info,
        [field]: value,
      },
    }));
  };

  const regenerateInvoiceNumber = () => {
    commitInvoice((current) => ({
      ...current,
      info: {
        ...current.info,
        invoiceNumber: createInvoiceNumber(),
      },
    }));
  };

  const updateCurrency = (currencyCode) => {
    const currency = getCurrencyOption(currencyCode);

    commitInvoice((current) => ({
      ...current,
      info: {
        ...current.info,
        currencyCode: currency.code,
        currencySymbol: currency.symbol,
        currencyName: currency.name,
      },
      terms: {
        ...current.terms,
        currency: `All amounts are in ${currency.name} unless otherwise stated.`,
      },
    }));
  };

  const updateTerms = (field, value) => {
    commitInvoice((current) => ({
      ...current,
      terms: {
        ...current.terms,
        [field]: value,
      },
    }));
  };

  const addItem = () => {
    commitInvoice((current) => ({
      ...current,
      items: [...current.items, createBlankItem()],
    }));
  };

  const removeItem = (itemId) => {
    commitInvoice((current) => ({
      ...current,
      items: current.items.filter((item) => item.id !== itemId),
    }));
  };

  const updateItem = (itemId, field, value) => {
    commitInvoice((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    }));
  };

  const resetInvoice = () => {
    if (
      hasEdited &&
      !window.confirm("Reset this invoice and clear entered information?")
    ) {
      return;
    }

    setInvoice(createDefaultInvoice());
    setHasEdited(false);
    setShowValidation(false);
  };

  const printInvoice = () => {
    if (validationMessages.length) {
      setShowValidation(true);
      window.alert(
        "Please resolve the highlighted invoice fields before printing.",
      );
      return;
    }

    flushSync(() => {
      setInvoice((current) => ({
        ...current,
        print: {
          printedAt: new Date().toISOString(),
        },
      }));
    });

    window.print();
  };

  const downloadInvoicePdf = async () => {
    if (validationMessages.length) {
      setShowValidation(true);
      window.alert(
        "Please resolve the highlighted invoice fields before downloading the PDF.",
      );
      return;
    }

    try {
      const qrElement = document.querySelector(".payment-qr");

      let qrDataUrl = "";

      if (qrElement) {
        qrDataUrl = qrElement.src || "";
      }

      const blob = await pdf(
        <InvoicePDF
          invoice={invoice}
          qrDataUrl={qrDataUrl}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${invoice.info.invoiceNumber}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate invoice PDF:", error);

      window.alert(
        "Unable to generate the invoice PDF. Please try again.",
      );
    }
  };

  return (
    <div className="invoice-app">
      <header className="app-header">
        <div>
          <p>Fortress Safe</p>
          <h1>Invoice Generator</h1>
        </div>

        <PrintActions
          onPrint={printInvoice}
          onReset={resetInvoice}
          onDownloadPdf={downloadInvoicePdf}
          hasErrors={validationMessages.length > 0}
        />
      </header>

      <main className="invoice-workspace">
        <aside className="form-panel">
          <InvoiceForm
            invoice={invoice}
            validationMessages={visibleValidationMessages}
            onUpdateClient={updateClient}
            onUpdateInfo={updateInfo}
            onRegenerateInvoiceNumber={regenerateInvoiceNumber}
            onUpdateCurrency={updateCurrency}
            onUpdateTerms={updateTerms}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onUpdateItem={updateItem}
          />
        </aside>

        <section className="preview-panel">
          <div className="preview-heading">
            <span>Live A4 preview</span>
          </div>

          <InvoicePreview invoice={invoice} />
        </section>
      </main>
    </div>
  );
}

export default App;